// components/wallet/TokenRedemption.jsx
// Updated: Dec 30, 2025 - Now queries tierProgress directly (consistent with LoyaltyTierBadge)
// Updated: Dec 31, 2025 - Added onLearnMore prop for 4.3 completion
// Shows PARTICIPATION-BASED loyalty tiers (not cart-based)
// Tiers unlock based on purchases + referrals
// Activity resets expiration clock

import React, { useState, useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';
import { 
  X, 
  ShoppingCart, 
  Send, 
  Store, 
  AlertCircle, 
  Coins, 
  Info, 
  ChevronRight,
  Star,
  Shield,
  Award,
  Crown,
  Zap,
  Clock,
  RefreshCw
} from 'lucide-react';
import Modal from '../common/Modal';
import { useTokens } from '../../context/TokenContext';
import { useNotification } from '../../context/NotificationContext';
import { useCart } from '../../context/CartContext';
import {
  TOKEN_CONFIG,
  formatTokens,
  formatTokenValue,
  tokensToUSD,
  calculateMaxRedemption,
  applyTokenRedemption,
  getNextLoyaltyTier,
  getTierProgress
} from '../../utils/tokenEconomy';

// Query tierProgress directly from backend for consistent data
const TIER_PROGRESS = gql`
  query TierProgress {
    tierProgress {
      currentTier
      currentLabel
      nextTier
      nextLabel
      purchasesNeeded
      referralsNeeded
      purchaseProgress
      referralProgress
      overallProgress
      isMaxTier
      message
    }
  }
`;

const TokenRedemption = ({ balance, onComplete, onLearnMore }) => {
  const [showModal, setShowModal] = useState(false);
  const [redemptionType, setRedemptionType] = useState('');
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  const { updateBalance, addTransaction, transferTokens } = useTokens();
  const { addNotification } = useNotification();
  const { getCartTotals, applyDiscount } = useCart();
  
  // Query tier progress directly from backend
  const { data: tierData } = useQuery(TIER_PROGRESS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore'
  });
  
  const cartTotals = getCartTotals();
  const cartTotal = cartTotals?.subtotal || 0;
  
  // Get tier info from backend query
  const tierProgress = tierData?.tierProgress;
  const userTier = tierProgress?.currentTier || 'NEW_USER';
  
  // Calculate purchases/referrals from progress data
  // If 2 more purchases needed for Bronze (5 required), user has 3 purchases
  const userPurchases = useMemo(() => {
    if (!tierProgress) return 0;
    if (tierProgress.isMaxTier) return 50; // Platinum
    
    // Reverse-calculate from purchasesNeeded
    const tierRequirements = {
      'NEW_USER': { nextTier: 'BRONZE', nextPurchases: 5 },
      'BRONZE': { nextTier: 'SILVER', nextPurchases: 15 },
      'SILVER': { nextTier: 'GOLD', nextPurchases: 30 },
      'GOLD': { nextTier: 'PLATINUM', nextPurchases: 50 },
      'PLATINUM': { nextTier: null, nextPurchases: 50 }
    };
    
    const req = tierRequirements[tierProgress.currentTier];
    if (req && tierProgress.purchasesNeeded !== undefined) {
      return req.nextPurchases - tierProgress.purchasesNeeded;
    }
    return 0;
  }, [tierProgress]);
  
  const userReferrals = useMemo(() => {
    if (!tierProgress) return 0;
    if (tierProgress.isMaxTier) return 10; // Platinum
    
    const tierRequirements = {
      'NEW_USER': { nextReferrals: 0 },
      'BRONZE': { nextReferrals: 2 },
      'SILVER': { nextReferrals: 5 },
      'GOLD': { nextReferrals: 10 },
      'PLATINUM': { nextReferrals: 10 }
    };
    
    const req = tierRequirements[tierProgress.currentTier];
    if (req && tierProgress.referralsNeeded !== undefined) {
      return req.nextReferrals - tierProgress.referralsNeeded;
    }
    return 0;
  }, [tierProgress]);
  
  // Calculate max redemption based on loyalty tier (not cart size)
  const redemptionInfo = useMemo(() => 
    calculateMaxRedemption(cartTotal, balance, userTier),
    [cartTotal, balance, userTier]
  );
  
  // Get next tier info
  const nextTierInfo = useMemo(() => 
    getNextLoyaltyTier(userTier),
    [userTier]
  );
  
  // Loyalty tier display data - TRUE HYBRID: tier % OR tier $ cap (whichever is LESS)
  const loyaltyTiers = [
    {
      tier: 'NEW_USER',
      label: 'New User',
      icon: Star,
      color: 'blue',
      percent: 0.10,
      maxUSD: 15,
      maxDisplay: '10% / $15',
      purchases: 0,
      referrals: 0,
      expiration: '90 days'
    },
    {
      tier: 'BRONZE',
      label: 'Bronze',
      icon: Shield,
      color: 'amber',
      percent: 0.15,
      maxUSD: 25,
      maxDisplay: '15% / $25',
      purchases: 5,
      referrals: 0,
      expiration: '180 days'
    },
    {
      tier: 'SILVER',
      label: 'Silver',
      icon: Award,
      color: 'slate',
      percent: 0.20,
      maxUSD: 40,
      maxDisplay: '20% / $40',
      purchases: 15,
      referrals: 2,
      expiration: '1 year'
    },
    {
      tier: 'GOLD',
      label: 'Gold',
      icon: Crown,
      color: 'yellow',
      percent: 0.30,
      maxUSD: 75,
      maxDisplay: '30% / $75',
      purchases: 30,
      referrals: 5,
      expiration: '2 years'
    },
    {
      tier: 'PLATINUM',
      label: 'Platinum',
      icon: Zap,
      color: 'purple',
      percent: 0.40,
      maxUSD: Infinity,
      maxDisplay: 'UNCAPPED 40%',
      purchases: 50,
      referrals: 10,
      expiration: 'Never'
    }
  ];
  
  // Get current tier data
  const currentTierData = loyaltyTiers.find(t => t.tier === userTier) || loyaltyTiers[0];
  const CurrentTierIcon = currentTierData.icon;
  
  const redemptionOptions = [
    {
      type: 'purchase',
      title: 'Use for Purchases',
      description: `Apply up to ${formatTokens(redemptionInfo.actualMaxTokens)} tokens (${formatTokenValue(redemptionInfo.actualMaxTokens)}) to your cart`,
      icon: ShoppingCart,
      color: 'blue',
      disabled: cartTotal <= 0
    },
    {
      type: 'transfer',
      title: 'Transfer to Member',
      description: 'Send tokens to another member',
      icon: Send,
      color: 'green',
      disabled: false
    },
    {
      type: 'vendor',
      title: 'Convert to Vendor Credit',
      description: 'Use with participating vendors',
      icon: Store,
      color: 'purple',
      disabled: false
    }
  ];
  
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  
  const handleRedemption = async () => {
    setError(null);
    
    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    const tokenAmount = parseInt(amount);
    
    if (tokenAmount > balance) {
      setError('Insufficient token balance');
      return;
    }
    
    if (redemptionType === 'transfer' && !recipient) {
      setError('Please enter recipient email');
      return;
    }
    
    if (redemptionType === 'transfer' && !validateEmail(recipient)) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Check redemption cap for purchases based on loyalty tier
    if (redemptionType === 'purchase') {
      if (tokenAmount > redemptionInfo.actualMaxTokens) {
        setError(`Your ${currentTierData.label} tier allows up to ${formatTokens(redemptionInfo.actualMaxTokens)} tokens ($${redemptionInfo.actualMaxValue.toFixed(2)}) per order`);
        return;
      }
    }
    
    setProcessing(true);
    
    try {
      switch (redemptionType) {
        case 'purchase':
          // Apply tokens to cart using token value
          const discountValue = tokensToUSD(tokenAmount);
          
          if (discountValue > cartTotal) {
            // Can't discount more than cart total
            const maxTokensForCart = Math.floor(cartTotal / TOKEN_CONFIG.VALUE_PER_TOKEN);
            const actualDiscount = tokensToUSD(maxTokensForCart);
            
            applyDiscount(actualDiscount);
            updateBalance(balance - maxTokensForCart);
            
            addTransaction({
              type: 'spend',
              category: 'purchase',
              amount: maxTokensForCart,
              description: 'Applied to cart purchase',
              status: 'completed'
            });
            
            addNotification({
              type: 'success',
              title: 'Tokens Applied',
              message: `${formatTokens(maxTokensForCart)} tokens applied for $${actualDiscount.toFixed(2)} off.`,
              showToast: true
            });
          } else {
            applyDiscount(discountValue);
            updateBalance(balance - tokenAmount);
            
            addTransaction({
              type: 'spend',
              category: 'purchase',
              amount: tokenAmount,
              description: 'Applied to cart purchase',
              status: 'completed'
            });
            
            addNotification({
              type: 'success',
              title: 'Tokens Applied',
              message: `${formatTokens(tokenAmount)} tokens applied for $${discountValue.toFixed(2)} off!`,
              showToast: true
            });
          }
          break;
          
        case 'transfer':
          // Transfer tokens
          await transferTokens(recipient, tokenAmount, `Transfer to ${recipient}`);
          
          addNotification({
            type: 'success',
            title: 'Transfer Successful',
            message: `${formatTokens(tokenAmount)} tokens sent to ${recipient}`,
            showToast: true
          });
          break;
          
        case 'vendor':
          // Convert to vendor credit
          const vendorCreditValue = tokensToUSD(tokenAmount);
          updateBalance(balance - tokenAmount);
          
          addTransaction({
            type: 'spend',
            category: 'vendor_credit',
            amount: tokenAmount,
            description: `Converted to $${vendorCreditValue.toFixed(2)} vendor credit`,
            status: 'completed'
          });
          
          // Store vendor credit
          const existingCredit = parseFloat(localStorage.getItem('vendor_credit') || '0');
          localStorage.setItem('vendor_credit', (existingCredit + vendorCreditValue).toString());
          
          addNotification({
            type: 'success',
            title: 'Credit Added',
            message: `${formatTokens(tokenAmount)} tokens converted to $${vendorCreditValue.toFixed(2)} vendor credit`,
            showToast: true
          });
          break;
      }
      
      if (onComplete) {
        onComplete({
          type: redemptionType,
          amount: tokenAmount,
          recipient: redemptionType === 'transfer' ? recipient : null
        });
      }
      
      resetForm();
      setShowModal(false);
    } catch (error) {
      setError(error.message || 'Redemption failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };
  
  const resetForm = () => {
    setAmount('');
    setRecipient('');
    setRedemptionType('');
    setError(null);
  };
  
  // Calculate max amount based on redemption type and loyalty tier
  const getMaxAmount = () => {
    if (redemptionType === 'purchase') {
      return redemptionInfo.actualMaxTokens;
    }
    return balance;
  };
  
  return (
    <>
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Coins className="w-6 h-6 text-yellow-500" />
          <h3 className="text-lg font-semibold">Redeem Tokens</h3>
        </div>
        <p className="text-gray-600 text-sm mb-4">
          Use your earned tokens for purchases, transfers, or vendor credit.
        </p>
        
        {/* Token Balance Display */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Available Balance</p>
              <p className="text-2xl font-bold text-gray-900">{formatTokens(balance)} tokens</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Value</p>
              <p className="text-xl font-bold text-green-600">{formatTokenValue(balance)}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            1 token = ${TOKEN_CONFIG.VALUE_PER_TOKEN.toFixed(2)}
          </p>
        </div>
        
        {/* Current Loyalty Tier Status */}
        <div className={`rounded-lg p-4 mb-4 ${currentTierData.tier === 'PLATINUM' ? 'bg-gradient-to-r from-purple-100 to-indigo-100 border border-purple-200' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentTierData.tier === 'PLATINUM' ? 'bg-purple-600 text-white' : 'bg-white border'}`}>
                <CurrentTierIcon size={20} className={currentTierData.tier === 'PLATINUM' ? 'text-white' : `text-${currentTierData.color}-600`} />
              </div>
              <div>
                <p className="font-bold text-gray-800">Your Tier: {tierProgress?.currentLabel || currentTierData.label}</p>
                <p className="text-xs text-gray-600">
                  {userPurchases} purchases • {userReferrals} referrals
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-700">Max per order:</p>
              <p className={`font-bold ${currentTierData.tier === 'PLATINUM' ? 'text-purple-600' : 'text-green-600'}`}>
                {currentTierData.maxDisplay}
              </p>
            </div>
          </div>
          
          {/* Progress to next tier - Using backend data */}
          {tierProgress && !tierProgress.isMaxTier && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-600 mb-1">{tierProgress.message}</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all" 
                  style={{ width: `${tierProgress.overallProgress}%` }}
                />
              </div>
            </div>
          )}
          
          {tierProgress?.isMaxTier && (
            <div className="mt-2 flex items-center gap-1 text-purple-600 text-sm">
              <Zap size={14} />
              <span>You've unlocked unlimited redemption!</span>
            </div>
          )}
        </div>
        
        {/* Redemption Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {redemptionOptions.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.type}
                onClick={() => {
                  if (!option.disabled) {
                    setRedemptionType(option.type);
                    setShowModal(true);
                  }
                }}
                disabled={option.disabled}
                className={`p-4 rounded-lg border-2 transition-all text-left
                  ${option.disabled 
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' 
                    : `border-${option.color}-200 hover:border-${option.color}-300 hover:shadow-md`
                  }`}
              >
                <Icon className={`text-${option.color}-600 mb-2 ${option.disabled ? 'opacity-50' : ''}`} size={24} />
                <h4 className="font-medium text-gray-900">{option.title}</h4>
                <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                {option.disabled && option.type === 'purchase' && (
                  <p className="text-xs text-orange-600 mt-2">Add items to cart first</p>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Loyalty Tiers Info - TRUE HYBRID System */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <Info size={14} className="text-gray-500" />
              <span className="text-xs font-medium text-gray-600">TRUE HYBRID: tier % OR tier $ cap (whichever is LESS)</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-green-600">
              <RefreshCw size={12} />
              <span>Activity resets expiration</span>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-1 text-xs">
            {loyaltyTiers.map((tier) => {
              const Icon = tier.icon;
              const isCurrentTier = tier.tier === userTier;
              return (
                <div 
                  key={tier.tier} 
                  className={`rounded p-2 text-center ${isCurrentTier ? 'bg-blue-100 border-2 border-blue-400' : 'bg-white'}`}
                >
                  <Icon size={16} className={`mx-auto mb-1 ${tier.tier === 'PLATINUM' ? 'text-purple-600' : `text-${tier.color}-600`}`} />
                  <p className={`font-semibold ${isCurrentTier ? 'text-blue-700' : 'text-gray-700'}`}>
                    {tier.label}
                  </p>
                  <p className={`${tier.tier === 'PLATINUM' ? 'text-purple-600' : 'text-green-600'} font-medium`}>
                    {tier.maxDisplay}
                  </p>
                  <p className="text-gray-400 text-[10px]">
                    {tier.purchases > 0 ? `${tier.purchases} buys` : 'Start'}
                    {tier.referrals > 0 ? ` + ${tier.referrals} refs` : ''}
                  </p>
                </div>
              );
            })}
          </div>
          {/* 4.3 Enhancement: Learn More button to expand Token Info */}
          <div className="flex items-center justify-center gap-2 mt-2">
            <p className="text-xs text-gray-500">
              💡 Progress through tiers: 10% → 15% → 20% → 30% → 40% UNCAPPED! Keep shopping & referring!
            </p>
            {onLearnMore && (
              <button
                type="button" onClick={(e) => { e.stopPropagation(); onLearnMore(); }}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline whitespace-nowrap"
              >
                Learn More →
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Redemption Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={`Redeem Tokens - ${redemptionType === 'purchase' ? 'Apply to Cart' : redemptionType === 'transfer' ? 'Transfer' : 'Vendor Credit'}`}
        size="medium"
      >
        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
              <AlertCircle className="text-red-600 mt-0.5 mr-2" size={16} />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tokens to Redeem
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              max={getMaxAmount()}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter amount"
            />
            <div className="flex justify-between mt-1">
              <p className="text-xs text-gray-500">
                Maximum: {formatTokens(getMaxAmount())} tokens
              </p>
              {amount && parseInt(amount) > 0 && (
                <p className="text-xs text-green-600">
                  Value: {formatTokenValue(parseInt(amount))}
                </p>
              )}
            </div>
            
            {/* Quick Amount Buttons */}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setAmount(Math.floor(getMaxAmount() / 4).toString())}
                className="flex-1 text-xs py-1 border rounded hover:bg-gray-50"
              >
                25%
              </button>
              <button
                type="button"
                onClick={() => setAmount(Math.floor(getMaxAmount() / 2).toString())}
                className="flex-1 text-xs py-1 border rounded hover:bg-gray-50"
              >
                50%
              </button>
              <button
                type="button"
                onClick={() => setAmount(Math.floor(getMaxAmount() * 0.75).toString())}
                className="flex-1 text-xs py-1 border rounded hover:bg-gray-50"
              >
                75%
              </button>
              <button
                type="button"
                onClick={() => setAmount(getMaxAmount().toString())}
                className="flex-1 text-xs py-1 border rounded hover:bg-gray-50 bg-yellow-50"
              >
                Max
              </button>
            </div>
          </div>
          
          {redemptionType === 'transfer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Email
              </label>
              <input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="member@example.com"
              />
            </div>
          )}
          
          {redemptionType === 'purchase' && (
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Your Tier:</strong> {tierProgress?.currentLabel || currentTierData.label} ({currentTierData.maxDisplay})
              </p>
              <p className="text-sm text-blue-800 mb-2">
                <strong>Your Cart:</strong> ${cartTotal.toFixed(2)} ({Math.round((currentTierData.percent || 0.10) * 100)}% = ${(cartTotal * (currentTierData.percent || 0.10)).toFixed(2)})
              </p>
              <p className="text-sm text-blue-800">
                <strong>You can use:</strong> {formatTokens(redemptionInfo.actualMaxTokens)} tokens (${redemptionInfo.actualMaxValue.toFixed(2)})
              </p>
              <p className="text-xs text-blue-600 mt-2 italic">
                {currentTierData.tier === 'PLATINUM' 
                  ? "Platinum = full 40% with no cap!" 
                  : `TRUE HYBRID: ${Math.round((currentTierData.percent || 0.10) * 100)}% OR $${currentTierData.maxUSD} (whichever is LESS)`}
              </p>
              {nextTierInfo && (
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <p className="text-xs text-blue-600 flex items-center gap-1">
                    <ChevronRight size={12} />
                    {nextTierInfo.message}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {redemptionType === 'vendor' && (
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-800">
                Vendor credit can be used with participating vendors for 
                direct purchases. Credit never expires.
              </p>
              {amount && parseInt(amount) > 0 && (
                <p className="text-sm text-purple-800 mt-2">
                  <strong>Credit Amount:</strong> ${tokensToUSD(parseInt(amount)).toFixed(2)}
                </p>
              )}
            </div>
          )}
          
          {/* Summary */}
          {amount && parseInt(amount) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="font-medium text-green-800">Redeeming:</span>
                <span className="font-bold text-green-700">
                  {formatTokens(parseInt(amount))} tokens = {formatTokenValue(parseInt(amount))}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1 text-sm">
                <span className="text-green-700">Remaining balance:</span>
                <span className="text-green-700">
                  {formatTokens(balance - parseInt(amount))} tokens
                </span>
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleRedemption}
              disabled={processing || !amount || parseInt(amount) <= 0}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {processing ? 'Processing...' : 'Confirm Redemption'}
            </button>
            <button
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TokenRedemption;